import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSystemLocations } from '@application/redux/gps-and-streams/gps-and-stream-slice';
// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
});
const DEFAULT_CENTER = [52.2653, 6.7905];
const cameraIcon = L.divIcon({
    className: '',
    html: `<div style="
    font-size: 33px;
  ">🎦</div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40]
});
function RecenterMap({ center, zoom, searchParams }) {
    const map = useMap();
    useEffect(() => {
        if (searchParams.size > 0) {
            map.setView(center, zoom);
        }
    }, [center, zoom, map]);
    return null;
}
function FitBoundsOnLoad({ locations }) {
    const map = useMap();
    useEffect(() => {
        if (locations.length > 0) {
            const bounds = L.latLngBounds(locations.map((loc) => [loc.latitude, loc.longitude]));
            map.fitBounds(bounds, { padding: [50, 50] });
        }
    }, [locations, map]);
    return null;
}
export default function MapViewer() {
    const [mapZoom, setMapZoom] = useState(11);
    const [searchParams] = useSearchParams();
    const defaultLocation = DEFAULT_CENTER;
    const [mapCenter, setMapCenter] = useState(defaultLocation);
    const [mainLocation, setMainLocation] = useState(defaultLocation);
    const [locationLabel, setLocationLabel] = useState('');
    const [loadedOnce, setLoadedOnce] = useState(false);
    const dispatch = useDispatch();
    const { systemLocation } = useSelector((state) => state.gpsAndStreams);
    const lat = searchParams.get('lat');
    const lon = searchParams.get('long');
    const loc = searchParams.get('loc');
    const showQueryParamLocation = lat && lon;
    function animateZoom(targetZoom, stepDelay = 500) {
        let current = mapZoom;
        if (current === targetZoom)
            return;
        const step = current < targetZoom ? 1 : -1;
        const interval = setInterval(() => {
            current += step;
            setMapZoom(current);
            if (current === targetZoom)
                clearInterval(interval);
        }, stepDelay);
    }
    function setDefaults() {
        setMapCenter(defaultLocation);
        setMainLocation(defaultLocation);
        setLocationLabel('Standaard locatie');
        setMapZoom(11);
    }
    function fallbackToUserLocation() {
        if (!navigator.geolocation) {
            setDefaults();
            return;
        }
        navigator.geolocation.getCurrentPosition((pos) => {
            const { latitude, longitude } = pos.coords;
            setMapCenter([latitude, longitude]);
            setMainLocation([latitude, longitude]);
            setMapZoom(11);
            fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`)
                .then((res) => res.json())
                .then((data) => {
                setLocationLabel(data.display_name || 'My Current Location');
            })
                .catch(() => setLocationLabel('My Current Location'));
            setTimeout(() => animateZoom(12, 600), 800);
        }, () => {
            setDefaults();
        });
    }
    useEffect(() => {
        if (showQueryParamLocation)
            return;
        const fetchData = async () => {
            try {
                const data = await dispatch(fetchSystemLocations()).unwrap();
                if (!data?.data || data.data.length === 0) {
                    fallbackToUserLocation();
                    return;
                }
                if (!loadedOnce) {
                    setLoadedOnce(true);
                    setTimeout(() => animateZoom(14, 400), 800);
                }
            }
            catch (error) {
                fallbackToUserLocation();
            }
        };
        fetchData();
        const intervalId = setInterval(fetchData, 30000);
        return () => clearInterval(intervalId);
    }, [dispatch, loadedOnce, lat, lon]);
    useEffect(() => {
        if (!showQueryParamLocation)
            return;
        if (showQueryParamLocation) {
            const parsedLat = parseFloat(lat);
            const parsedLon = parseFloat(lon);
            setMapCenter([parsedLat, parsedLon]);
            setMainLocation([parsedLat, parsedLon]);
            setLocationLabel(loc || 'Opgegeven locatie');
            setMapZoom(11);
            setTimeout(() => animateZoom(14, 400), 800);
        }
    }, [searchParams, systemLocation]);
    useEffect(() => {
        if (!showQueryParamLocation &&
            (!systemLocation || !systemLocation.data || systemLocation.data.length === 0)) {
            fallbackToUserLocation();
        }
    }, [showQueryParamLocation, systemLocation]);
    return (_jsx("div", { style: { height: '100vh', width: '100%' }, children: _jsxs(MapContainer, { center: mapCenter, zoom: mapZoom, style: { height: '100%', width: '100%' }, children: [_jsx(TileLayer, { url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '\u00A9 OpenStreetMap contributors' }), !showQueryParamLocation && systemLocation?.data.length > 0 && (_jsx(FitBoundsOnLoad, { locations: systemLocation.data })), _jsx(RecenterMap, { center: mapCenter, zoom: mapZoom, searchParams: searchParams }), !showQueryParamLocation &&
                    systemLocation?.data.map((car, index) => (_jsx(Marker, { position: [car.latitude, car.longitude], icon: cameraIcon, children: _jsx(Popup, { children: car.systemName }) }, `car-${index}`))), _jsx(Marker, { position: mainLocation, children: _jsx(Popup, { children: locationLabel }, locationLabel) })] }) }));
}
